import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

/**
 * Context globale per il prompt di installazione della PWA.
 *
 * - `beforeinstallprompt` viene emesso dal browser quando la pagina è
 *   considerata installabile (manifest + service worker + HTTPS + scope).
 * - Lo catturo a livello di Root (montato in tutta l'app) così la Navbar
 *   può mostrare "Installa App" anche quando l'utente è su pagine
 *   diverse dal login.
 * - Una volta installata l'app, `appinstalled` viene emesso e il flag
 *   `isInstalled` diventa true; il prompt viene rimosso dallo state.
 * - `display-mode: standalone` rileva quando la pagina è già aperta come
 *   PWA installata (es. lo staff apre l'icona desktop): in quel caso
 *   nascondiamo proattivamente il bottone.
 *
 * Detection piattaforma (Feb 2026, richiesta utente):
 * - `platform`: "desktop" | "android" | "ios" | "other"
 * - `isIOSSafari`: serve banner manuale ("Condividi → Aggiungi a Home")
 *   perché Apple non espone `beforeinstallprompt`. UI mostra istruzioni
 *   guidate al posto del prompt automatico.
 * - `installLabel`: label dinamica per il bottone install. Distingue
 *   desktop vs telefono per chiarezza al medico.
 */
const PwaInstallContext = createContext({
  canInstall: false,
  isInstalled: false,
  promptInstall: async () => false,
  platform: "other",
  isIOSSafari: false,
  isIOSNonSafari: false,
  isMobileDevice: false,
  installLabel: "Installa App",
});

function detectPlatform() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { platform: "other", isIOSSafari: false, isIOSNonSafari: false, isMobileDevice: false };
  }
  const ua = (navigator.userAgent || "").toLowerCase();
  // iPad moderni (iPadOS 13+) si presentano come "MacIntel" con touch — gestiti.
  const isIPadDesktopUA =
    navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  const isIOS = /iphone|ipad|ipod/i.test(ua) || isIPadDesktopUA;
  if (isIOS) {
    // Su iOS solo Safari supporta "Aggiungi a Home" via Share menu.
    // Chrome/Firefox/Edge su iOS sono webkit-wrapped (Apple policy) e NON
    // possono installare PWA direttamente. Detection:
    //   - Safari iOS: UA ha "version/.*safari" e NON ha CriOS/FxiOS/EdgiOS
    //   - Chrome iOS: UA ha "crios"
    //   - Firefox iOS: UA ha "fxios"
    //   - Edge iOS: UA ha "edgios"
    const isCriOS = /crios/i.test(ua);
    const isFxiOS = /fxios/i.test(ua);
    const isEdgiOS = /edgios/i.test(ua);
    const isNonSafariBrowser = isCriOS || isFxiOS || isEdgiOS;
    const isSafari = /version\/[\d.]+.*safari/i.test(ua) && !isNonSafariBrowser;
    // Solo iPhone è "telefono" (escludiamo iPad). Detection: UA include
    // "iphone" OPPURE "ipod". iPad → tablet → escluso da mobile.
    const isIPhone = /iphone|ipod/i.test(ua);
    return {
      platform: "ios",
      isIOSSafari: isSafari,
      isIOSNonSafari: isNonSafariBrowser,
      isMobileDevice: isIPhone,
    };
  }
  if (/android/i.test(ua)) {
    // Android "phone" vs "tablet": Google indica che gli smartphone Android
    // hanno la stringa "Mobile" nel UA, i tablet NON ce l'hanno. Ref:
    // https://developer.chrome.com/blog/user-agent-reduction-android-model-and-version/
    const isAndroidPhone = /mobile/i.test(ua);
    return { platform: "android", isIOSSafari: false, isIOSNonSafari: false, isMobileDevice: isAndroidPhone };
  }
  // Tutto il resto: desktop (windows, mac chrome/firefox/edge, linux).
  return { platform: "desktop", isIOSSafari: false, isIOSNonSafari: false, isMobileDevice: false };
}

export function PwaInstallProvider({ children }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [{ platform, isIOSSafari, isIOSNonSafari, isMobileDevice }] = useState(detectPlatform);

  useEffect(() => {
    // Registra il service worker globale una sola volta. Chrome richiede
    // SW attivo + manifest per esporre `beforeinstallprompt`, quindi è
    // necessario qui (a livello Root) per rendere installabile la PWA
    // da QUALSIASI pagina (login incluso).
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        .catch(() => { /* non bloccante */ });
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallEvent(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    // Già aperta come PWA standalone → nascondi il bottone.
    // Su iOS la proprietà sta su `navigator.standalone`, non sul media query.
    const isStandalone =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setIsInstalled(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installEvent) return false;
    installEvent.prompt();
    try {
      const choice = await installEvent.userChoice;
      setInstallEvent(null);
      return choice?.outcome === "accepted";
    } catch {
      return false;
    }
  }, [installEvent]);

  // Label dinamica per smartphone:
  //   - Android Chrome → "Installa app sul telefono" (prompt nativo)
  //   - iPhone Safari → "Installa sul tuo iPhone" (apre modal istruzioni Add-to-Home)
  //   - iPhone Chrome/Firefox/Edge → "Apri in Safari per installare" (modal
  //     guida l'utente ad aprire la pagina in Safari, unico browser iOS
  //     che può installare PWA per limitazione Apple)
  //   - Desktop → "Installa app sul desktop" (mai mostrato da Navbar perché
  //     gated da isMobileDevice, ma mantenuto come fallback per il context)
  const installLabel = useMemo(() => {
    if (platform === "android") return "Installa app sul telefono";
    if (platform === "ios") {
      if (isIOSNonSafari) return "Apri in Safari per installare";
      return "Installa sul tuo iPhone";
    }
    return "Installa app sul desktop";
  }, [platform, isIOSNonSafari]);

  // canInstall combina TRE casi:
  //   1. Evento nativo `beforeinstallprompt` (Chrome desktop/Android)
  //   2. iOS Safari → mostra modal con istruzioni Add-to-Home
  //   3. iOS Chrome/Firefox/Edge → mostra modal con istruzioni "Apri in Safari"
  // In tutti i casi è il consumer (Navbar) che decide cosa fare al click in
  // base a `isIOSSafari` / `isIOSNonSafari`.
  const canInstall = !isInstalled && (!!installEvent || isIOSSafari || isIOSNonSafari);

  return (
    <PwaInstallContext.Provider
      value={{
        canInstall,
        isInstalled,
        promptInstall,
        platform,
        isIOSSafari,
        isIOSNonSafari,
        isMobileDevice,
        installLabel,
      }}
    >
      {children}
    </PwaInstallContext.Provider>
  );
}

export function usePwaInstall() {
  return useContext(PwaInstallContext);
}
